import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Divider,
} from "@mui/material";
import getSymbolFromCurrency from "currency-symbol-map";
import { sortedAllowanceEntries, formatAllowanceGrant } from "../../constants/planConstants";
import { formatPrice } from "../../utils/planHelpers";
import styles from "./style";
import { formatDate } from "../../utils/DateFormat";

// Amount and currency are rendered exactly as the backend supplied them.
// This never multiplies price by quantity — the checkout response is the only
// authoritative source of the payable amount.
export const formatMoney = (amount, currency) => {
  if (amount === null || amount === undefined || amount === "") return "—";
  const code = String(currency || "").toUpperCase();
  const symbol = code ? getSymbolFromCurrency(code) : "";
  const value = formatPrice(amount);
  if (symbol) return `${symbol}${value}`;
  return code ? `${code} ${value}` : value;
};

// Client-side guard only; the backend remains authoritative and its rejection
// message is surfaced verbatim when it disagrees.
export const validateQuantity = (pack, quantity) => {
  if (pack?.packType !== "per_unit") return "";

  const raw = String(quantity ?? "").trim();
  if (raw === "") return "Enter how many you need.";
  if (!/^\d+$/.test(raw)) return "Quantity must be a whole number.";

  const value = Number(raw);
  const min = Number(pack?.minQuantity);
  const max = Number(pack?.maxQuantity);

  if (Number.isFinite(min) && value < min) {
    return `Quantity must be at least ${min}.`;
  }
  if (Number.isFinite(max) && value > max) {
    return `Quantity must be at most ${max}.`;
  }
  return "";
};

const TopupPurchaseDialog = ({
  open = false,
  pack = null,
  quote = null,
  submitting = false,
  error = "",
  onQuantityCheckout,
  onProceedToPayment,
  onClose,
}) => {
  const isPerUnit = pack?.packType === "per_unit";
  const [quantity, setQuantity] = useState("");
  const [quantityError, setQuantityError] = useState("");

  useEffect(() => {
    if (!open) return;
    setQuantityError("");
    // A fixed pack's quantity is defined by the pack itself and is not editable.
    setQuantity(isPerUnit ? String(pack?.minQuantity ?? 1) : "");
  }, [open, pack, isPerUnit]);

  const price = pack?.priceForCountry;

  const handleGetQuote = () => {
    const message = validateQuantity(pack, quantity);
    if (message) {
      setQuantityError(message);
      return;
    }
    setQuantityError("");
    onQuantityCheckout(pack, isPerUnit ? Number(quantity) : undefined);
  };

  if (!pack) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={styles.title}>Confirm your topup</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={styles.alert}>
            {error}
          </Alert>
        )}

        <Box sx={styles.row}>
          <Typography sx={styles.label}>Pack</Typography>
          <Typography sx={styles.value}>{pack?.name || "—"}</Typography>
        </Box>
        <Box sx={styles.row}>
          <Typography sx={styles.label}>Adds</Typography>
          <Box>
            {sortedAllowanceEntries(pack?.allowances).length === 0 ? (
              <Typography sx={styles.value}>—</Typography>
            ) : (
              sortedAllowanceEntries(pack?.allowances).map(([key, value]) => {
                const { label, display } = formatAllowanceGrant(key, value);
                return (
                  <Typography key={key} sx={styles.value}>
                    {label}: {display}
                  </Typography>
                );
              })
            )}
          </Box>
        </Box>

        {isPerUnit && !quote && (
          <Box sx={styles.quantityBox}>
            <Typography sx={styles.label}>Quantity</Typography>
            <TextField
              size="small"
              type="number"
              value={quantity}
              onChange={(e) => {
                setQuantity(e.target.value);
                setQuantityError("");
              }}
              error={!!quantityError}
              helperText={
                quantityError ||
                `Choose between ${pack?.minQuantity ?? "—"} and ${pack?.maxQuantity ?? "—"}`
              }
              inputProps={{
                min: pack?.minQuantity,
                max: pack?.maxQuantity,
                step: 1,
                "aria-label": "Quantity",
              }}
              sx={styles.quantityInput}
            />
          </Box>
        )}

        {/* A fixed pack has no quantity of its own — what it grants is shown
            entirely by the "Adds" list above, so this row only ever applies
            to a per_unit pack. */}
        {isPerUnit && quote && (
          <Box sx={styles.row}>
            <Typography sx={styles.label}>Quantity</Typography>
            <Typography sx={styles.value} data-testid="review-quantity">
              {quote?.quantity ?? quantity}
            </Typography>
          </Box>
        )}

        <Divider sx={styles.divider} />

        {quote ? (
          <>
            {/* Everything below comes from the checkout response. */}
            <Box sx={styles.row}>
              <Typography sx={styles.label}>Total payable</Typography>
              <Typography sx={styles.total} data-testid="review-amount">
                {formatMoney(quote?.amount, quote?.currency)}
              </Typography>
            </Box>
            <Typography sx={styles.note} data-testid="review-expiry">
              Valid until {formatDate(quote?.expiresAt)}. Topups expire with your
              current billing cycle and do not renew automatically.
            </Typography>
          </>
        ) : (
          <>
            <Box sx={styles.row}>
              <Typography sx={styles.label}>
                {isPerUnit ? "Price per unit" : "Price"}
              </Typography>
              <Typography sx={styles.value} data-testid="review-unit-price">
                {formatMoney(price?.price, price?.currency)}
              </Typography>
            </Box>
            <Typography sx={styles.note}>
              The total payable is confirmed by CabLinks on the next step.
            </Typography>
          </>
        )}
      </DialogContent>

      <DialogActions sx={styles.actions}>
        <Button onClick={onClose} disabled={submitting} sx={styles.cancel}>
          Cancel
        </Button>
        {quote ? (
          <Button
            variant="contained"
            onClick={onProceedToPayment}
            disabled={submitting}
            sx={styles.confirm}
          >
            Proceed to payment
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={handleGetQuote}
            disabled={submitting}
            sx={styles.confirm}
            startIcon={submitting ? <CircularProgress size={14} /> : null}
          >
            Continue
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default TopupPurchaseDialog;
